/**
 * OnboardingWizard — first-login flow per Dev Brief 2026-02-27.
 *
 * Section 1 (About You, 3 screens) -> creates a match + flips it to
 * user_persona via POST /api/matches/set-user-persona.
 * Section 2 (Your First Relationship, 4 screens) -> creates a second match,
 * then navigates to /eroscoach/{matchId}.
 *
 * Brief rules of note (see spec §2):
 *  - Only `name` is required in each section; everything else is skippable.
 *  - No "Skip / I don't know" controls anywhere.
 *  - `age` recalculated from DOB when both present (DOB wins).
 *  - `persona_connection` never defaults to Union; unset -> coerce to 1 (Spark) on save (Q6=b).
 *  - No em dashes in copy.
 *  - No dismiss (Q7=y).
 */
import { useEffect, useMemo, useRef, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import { authAxios } from "../utils/auth";
import { resolveAge, parseMDY, isValidTime } from "../utils/ageFromDOB";
import { emitWizardEvent } from "../utils/onboardingTrigger";
import { AuthContext } from "../App";
import OnboardingWelcome from "../components/rewards/OnboardingWelcome";
import OnboardingComplete from "../components/rewards/OnboardingComplete";
import {
  RELATIONSHIP_TYPES,
  PERSONA_CONNECTION_LEVELS,
} from "../constants/relationshipOptions";

// Same controlled-vocabulary lists used by /edit and /edit-section.
const SEX_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "trans_male", label: "Trans Male" },
  { value: "trans_female", label: "Trans Female" },
  { value: "unknown", label: "Unknown" },
];
const PRONOUN_OPTIONS = [
  { value: "he_him", label: "He/Him" },
  { value: "she_her", label: "She/Her" },
  { value: "they_them", label: "They/Them" },
  { value: "custom", label: "Other / Custom" },
];
const ORIENTATION_OPTIONS = [
  { value: "straight", label: "Straight" },
  { value: "gay", label: "Gay" },
  { value: "lesbian", label: "Lesbian" },
  { value: "bisexual", label: "Bisexual" },
  { value: "pansexual", label: "Pansexual" },
  { value: "asexual", label: "Asexual" },
  { value: "unknown", label: "Unknown" },
];

const inputClass =
  "w-full bg-white/5 border border-divine-gold/30 text-white rounded-lg px-3 py-2 focus:outline-none focus:border-divine-gold/70 transition-colors";
// Selects use a SOLID background so the native dropdown popup inherits a
// dark surface (Chrome/Edge/Safari render the open option list against the
// select's background colour; bg-white/5 + text-white = invisible options).
const selectClass =
  "w-full bg-divine-black border border-divine-gold/30 text-white rounded-lg px-3 py-2 focus:outline-none focus:border-divine-gold/70 transition-colors appearance-none";
// Each <option> also gets explicit colours so browsers that ignore the
// parent <select> bg (Firefox on some platforms, Safari iOS) still render
// the popup correctly.
const optionClass = "bg-divine-black text-white";
const labelClass = "block text-divine-gold/80 text-sm mb-1 font-medium";
const helpClass = "text-white/40 text-xs mt-1 italic";
const errorClass = "text-red-400 text-xs mt-1";
const inputErrorBorder = "border-red-400/70 focus:border-red-400";

function Field({ label, helper, error, children, testid }) {
  return (
    <div className="mb-4" data-testid={testid}>
      <label className={labelClass}>{label}</label>
      {children}
      {error ? (
        <p className={errorClass} data-testid={`${testid}-error`}>{error}</p>
      ) : (
        helper && <p className={helpClass}>{helper}</p>
      )}
    </div>
  );
}

// Section-level screen counts. Section 1 dropped DOB/TOB and merged the
// living/birth-location screen into the age screen, so it's 3 screens now.
// Section 2 still has its original 4-screen structure.
const SECTION_SCREEN_COUNTS = { 1: 3, 2: 4 };
const SECTION_TOTAL_SCREENS = SECTION_SCREEN_COUNTS[1] + SECTION_SCREEN_COUNTS[2]; // 7

function ProgressDots({ section, screen }) {
  const offset = section === 1 ? 0 : SECTION_SCREEN_COUNTS[1];
  return (
    <div className="flex justify-center gap-1.5 mb-6" data-testid="wizard-progress-dots">
      {Array.from({ length: SECTION_TOTAL_SCREENS }).map((_, i) => {
        // Per brief: dots represent COMPLETED steps. After Section 1 finishes
        // and the user lands on Section 2 screen 1, exactly the Section 1
        // count of dots is gold. The current screen is NOT filled.
        const active = i < offset + screen - 1;
        return (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-all ${
              active ? "bg-divine-gold w-6" : "bg-white/15 w-1.5"
            }`}
          />
        );
      })}
    </div>
  );
}

// 13 clickable stage cards for persona_connection. Selected card gets gold ring.
function StageCardGrid({ value, onChange }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="stage-card-grid">
      {PERSONA_CONNECTION_LEVELS.map((lvl) => {
        const selected = value === lvl.value;
        return (
          <button
            type="button"
            key={lvl.value}
            onClick={() => onChange(lvl.value)}
            data-testid={`stage-card-${lvl.value}`}
            className={`text-left rounded-lg px-3 py-2 border transition-all ${
              selected
                ? "border-divine-gold bg-divine-gold/15 ring-1 ring-divine-gold/60"
                : "border-white/10 bg-white/5 hover:border-divine-gold/40 hover:bg-white/10"
            }`}
          >
            <div className="flex items-center gap-2">
              <span className="text-divine-gold text-xs font-semibold w-6 shrink-0">
                L{lvl.value}
              </span>
              <span className="text-white text-sm font-medium">{lvl.title}</span>
            </div>
            <p className="text-white/55 text-xs mt-0.5 leading-snug">
              {lvl.description}
            </p>
          </button>
        );
      })}
    </div>
  );
}

function emptyForm() {
  return {
    name: "",
    sex: "",
    pronouns: "",
    // Free-text pronouns entered when `pronouns === "custom"`. On submit,
    // if this is non-empty it replaces the sentinel `"custom"` in the
    // payload so the backend stores the actual text (e.g. "xe/xir").
    pronouns_custom: "",
    sexual_orientation: "",
    date_of_birth: "",
    time_of_birth: "",
    age: "",
    living_location: "",
    birth_location: "",
    // section 2 only
    relationship_status: "",
    persona_connection: null,
  };
}

function filledKeys(form) {
  return Object.entries(form)
    .filter(([k, v]) => {
      if (k === "persona_connection") return v !== null && v !== "";
      // pronouns_custom is a UI-only field — it's folded into pronouns on submit.
      if (k === "pronouns_custom") return false;
      return v !== "" && v !== null && v !== undefined;
    })
    .map(([k]) => k);
}

/**
 * Fold the "custom" sentinel from the pronouns select into the actual
 * free-text value the user typed. If they picked custom but left the text
 * blank, submit `undefined` rather than the meaningless sentinel string.
 */
function resolvePronouns(form) {
  if (form.pronouns === "custom") {
    const custom = (form.pronouns_custom || "").trim();
    return custom || undefined;
  }
  return form.pronouns || undefined;
}

/**
 * Validate the four date/time inputs in the wizard. Returns an `errors`
 * object keyed by `<section>_<field>` ("s1_dob", "s2_tob", etc.). A blank
 * value never produces an error (fields are optional per brief §2).
 *  - dob: must match strict MM/DD/YYYY with a real calendar date (parseMDY).
 *  - tob: must match strict HH:MM 24-hour (isValidTime).
 */
function validateDateTime(form1, form2) {
  const errs = {};
  if (form1.date_of_birth && !parseMDY(form1.date_of_birth)) {
    errs.s1_dob = "Use format MM/DD/YYYY.";
  }
  if (form1.time_of_birth && !isValidTime(form1.time_of_birth)) {
    errs.s1_tob = "Use 24-hour format HH:MM.";
  }
  if (form2.date_of_birth && !parseMDY(form2.date_of_birth)) {
    errs.s2_dob = "Use format MM/DD/YYYY.";
  }
  if (form2.time_of_birth && !isValidTime(form2.time_of_birth)) {
    errs.s2_tob = "Use 24-hour format HH:MM.";
  }
  return errs;
}

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const userFirstName = useMemo(() => {
    const n = (auth?.user?.name || "").trim();
    if (!n) return "";
    return n.split(/\s+/)[0];
  }, [auth]);

  // section: 1 or 2. screen: 1..4
  const [section, setSection] = useState(1);
  const [screen, setScreen] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [skipSection1, setSkipSection1] = useState(false);
  // Bookend flow stage: "welcome" | "wizard" | "closing". Wraps the
  // existing section-1 / section-2 screens between two ceremony screens.
  const [flowStage, setFlowStage] = useState("welcome");
  const [closingMatchId, setClosingMatchId] = useState(null);
  const [closingPartnerName, setClosingPartnerName] = useState("them");

  // Two independent form objects.
  const [form1, setForm1] = useState(() => ({ ...emptyForm(), name: "" }));
  const [form2, setForm2] = useState(emptyForm);
  // Validation errors for the four MM/DD/YYYY / HH:MM text inputs.
  // Keys: s1_dob, s1_tob, s2_dob, s2_tob. Absent key = no error.
  const [errors, setErrors] = useState({});

  const f = section === 1 ? form1 : form2;
  const setF = section === 1 ? setForm1 : setForm2;
  const intro =
    section === 1
      ? ""
      : "Now tell us about the relationship you'd like advice on. This builds a working model of the connection, always from your point of view, never presented as fact about the other person. Like your own profile, this is a foundation that Agent Eros builds upon during every coaching session, so you can refine all of it later. Skip any fields you cannot fill out.";

  // Initial mount: pre-check whether user already has a user-persona (in case
  // they hit /onboarding via deep link). If yes, skip Section 1.
  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    (async () => {
      try {
        const res = await authAxios.get("/matches");
        const list = Array.isArray(res.data) ? res.data : res.data?.matches || [];
        const hasUP = list.some((m) => m.match_type === "user_persona");
        const hasMP = list.some((m) => m.match_type !== "user_persona");
        if (hasUP) {
          setSkipSection1(true);
          setFlowStage("wizard");   // returning users bypass the welcome ceremony
          if (hasMP) {
            // Both already exist — bail entirely to coach.
            const mp = list.find((m) => m.match_type !== "user_persona");
            navigate(`/eroscoach/${mp.match_id}`, { replace: true });
            return;
          }
          setSection(2);
          setScreen(1);
        }
        // Seed Section 1 name from auth context if blank.
        if (!form1.name && userFirstName) {
          setForm1((p) => ({ ...p, name: userFirstName }));
        }
      } catch (_) {
        /* non-fatal */
      }
    })();
  }, []);

  // DOB <-> age recalc: whenever DOB changes OR the user types age while DOB
  // is present, enforce DOB-as-source-of-truth (Global Rule 7). When DOB is
  // unparseable / blank, leave whatever the user typed in `age` alone.
  useEffect(() => {
    const resolved = resolveAge(f.date_of_birth, f.age);
    if (resolved !== null && String(resolved) !== String(f.age)) {
      setF((prev) => ({ ...prev, age: String(resolved) }));
    }
  }, [f.date_of_birth, f.age]);

  // Emit view event on each screen mount.
  useEffect(() => {
    emitWizardEvent({ event: "view", section, screen });
  }, [section, screen]);

  // Re-run validation whenever a date/time field changes.
  useEffect(() => {
    setErrors(validateDateTime(form1, form2));
  }, [
    form1.date_of_birth,
    form1.time_of_birth,
    form2.date_of_birth,
    form2.time_of_birth,
  ]);

  /** Returns true if the current section has no blocking date/time errors. */
  const sectionIsValid = () => {
    const errs = validateDateTime(form1, form2);
    if (section === 1) return !errs.s1_dob && !errs.s1_tob;
    return !errs.s2_dob && !errs.s2_tob;
  };

  const goNext = () => {
    if (!sectionIsValid()) {
      setErrors(validateDateTime(form1, form2));
      toast.error("Please fix the highlighted fields or clear them.");
      return;
    }
    emitWizardEvent({
      event: "next",
      section,
      screen,
      filled_field_count: filledKeys(f).length,
    });
    setScreen((s) => Math.min(s + 1, 4));
  };
  const goBack = () => {
    emitWizardEvent({ event: "back", section, screen });
    setScreen((s) => Math.max(s - 1, 1));
  };

  // ---------- submit handlers ----------
  const submitSection1 = async () => {
    if (!form1.name.trim()) {
      toast.error("Please enter a name to continue.");
      return;
    }
    if (!sectionIsValid()) {
      setErrors(validateDateTime(form1, form2));
      toast.error("Please fix the highlighted fields or clear them.");
      return;
    }
    setSubmitting(true);
    try {
      // Convert MM/DD/YYYY -> ISO YYYY-MM-DD before save (backend accepts string).
      const dobIso = form1.date_of_birth ? parseMDY(form1.date_of_birth) : undefined;
      const payload = {
        name: form1.name.trim(),
        relationship_status: "friend", // backend requires a value; placeholder
        sex: form1.sex || undefined,
        pronouns: resolvePronouns(form1),
        sexual_orientation: form1.sexual_orientation || undefined,
        date_of_birth: dobIso,
        time_of_birth: form1.time_of_birth || undefined,
        age: form1.age ? parseInt(form1.age, 10) : undefined,
        living_location: form1.living_location || undefined,
        birth_location: form1.birth_location || undefined,
      };
      const created = await authAxios.post("/matches", payload);
      const matchId = created.data?.match_id;
      if (!matchId) throw new Error("Match creation failed.");
      await authAxios.post("/matches/set-user-persona", { match_id: matchId });
      emitWizardEvent({
        event: "submit",
        section: 1,
        screen: 4,
        filled_field_count: filledKeys(form1).length,
        extra: { filled_fields: filledKeys(form1) },
      });
      // Section gate: if a match_persona already exists, jump to coach.
      const list = await authAxios.get("/matches");
      const matches = Array.isArray(list.data) ? list.data : list.data?.matches || [];
      const existingMP = matches.find((m) => m.match_type !== "user_persona");
      if (existingMP) {
        emitWizardEvent({ event: "complete", section: 1, screen: 4, extra: { gated: true } });
        navigate(`/eroscoach/${existingMP.match_id}`, { replace: true });
        return;
      }
      setSection(2);
      setScreen(1);
    } catch (e) {
      toast.error(`Could not save: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const submitSection2 = async () => {
    if (!form2.name.trim()) {
      toast.error("Please enter a name to continue.");
      return;
    }
    if (!sectionIsValid()) {
      setErrors(validateDateTime(form1, form2));
      toast.error("Please fix the highlighted fields or clear them.");
      return;
    }
    setSubmitting(true);
    try {
      const dobIso = form2.date_of_birth ? parseMDY(form2.date_of_birth) : undefined;
      const payload = {
        name: form2.name.trim(),
        relationship_status: form2.relationship_status || "friend",
        sex: form2.sex || undefined,
        pronouns: resolvePronouns(form2),
        sexual_orientation: form2.sexual_orientation || undefined,
        // Q6 = b: unset -> 1 (Spark), never null.
        persona_connection:
          form2.persona_connection !== null && form2.persona_connection !== ""
            ? Number(form2.persona_connection)
            : 1,
        date_of_birth: dobIso,
        time_of_birth: form2.time_of_birth || undefined,
        age: form2.age ? parseInt(form2.age, 10) : undefined,
        living_location: form2.living_location || undefined,
        birth_location: form2.birth_location || undefined,
      };
      const created = await authAxios.post("/matches", payload);
      const matchId = created.data?.match_id;
      if (!matchId) throw new Error("Match creation failed.");
      emitWizardEvent({
        event: "submit",
        section: 2,
        screen: 4,
        filled_field_count: filledKeys(form2).length,
        extra: { filled_fields: filledKeys(form2) },
      });
      emitWizardEvent({
        event: "complete",
        section: 2,
        screen: 4,
        extra: {
          total_filled_fields:
            filledKeys(form1).length + filledKeys(form2).length,
          has_dob: !!form1.date_of_birth || !!form2.date_of_birth,
          has_persona_connection: form2.persona_connection !== null,
        },
      });
      // Bookend: show the closing coin-reveal ceremony before navigating.
      setClosingMatchId(matchId);
      setClosingPartnerName(form2.name || "them");
      setFlowStage("closing");
    } catch (e) {
      toast.error(`Could not save: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- screen renderers ----------
  const update = (key) => (e) =>
    setF((prev) => ({ ...prev, [key]: e.target.value }));
  const updateRaw = (key, val) => setF((prev) => ({ ...prev, [key]: val }));

  const relStatusDescription = useMemo(() => {
    if (section !== 2 || !form2.relationship_status) return "";
    const opt = RELATIONSHIP_TYPES.find((r) => r.value === form2.relationship_status);
    if (!opt) return "";
    const matchName = form2.name?.trim() || "they";
    const userName = form1.name?.trim() || (userFirstName || "you");
    return opt.description.replaceAll("{match}", matchName).replaceAll("{user}", userName);
  }, [section, form2.relationship_status, form2.name, form1.name, userFirstName]);

  const renderSection1 = () => {
    if (screen === 1) {
      return (
        <>
          <Field label="Your name or an alias you would like to use with Agent Eros?" testid="field-s1-name">
            <input
              data-testid="s1-name-input"
              className={inputClass}
              value={form1.name}
              onChange={update("name")}
              placeholder="e.g. Frankie"
            />
          </Field>
          <Field label="What is your biological sex?" testid="field-s1-sex">
            <select className={selectClass} value={form1.sex} onChange={update("sex")} data-testid="s1-sex-select">
              <option value="" className={optionClass}>Select...</option>
              {SEX_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} className={optionClass}>{o.label}</option>
              ))}
            </select>
          </Field>
        </>
      );
    }
    if (screen === 2) {
      return (
        <>
          <Field label="How do you prefer to be addressed?" testid="field-s1-pronouns">
            <select className={selectClass} value={form1.pronouns} onChange={update("pronouns")} data-testid="s1-pronouns-select">
              <option value="" className={optionClass}>Select...</option>
              {PRONOUN_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} className={optionClass}>{o.label}</option>
              ))}
            </select>
            {form1.pronouns === "custom" && (
              <input
                className={`${inputClass} mt-2`}
                value={form1.pronouns_custom}
                onChange={update("pronouns_custom")}
                placeholder="e.g. xe/xir, ze/zir, name only"
                data-testid="s1-pronouns-custom-input"
                autoFocus
              />
            )}
          </Field>
          <Field label="What is your sexual orientation?" testid="field-s1-orientation">
            <select className={selectClass} value={form1.sexual_orientation} onChange={update("sexual_orientation")} data-testid="s1-orientation-select">
              <option value="" className={optionClass}>Select...</option>
              {ORIENTATION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} className={optionClass}>{o.label}</option>
              ))}
            </select>
          </Field>
        </>
      );
    }
    // Screen 3 (final section-1 screen) — age + birth location + living location.
    // DOB / time-of-birth were removed per product simplification.
    return (
      <>
        <Field label="How old are you?" testid="field-s1-age">
          <input
            type="number"
            min="0"
            max="130"
            className={inputClass}
            value={form1.age}
            onChange={update("age")}
            data-testid="s1-age-input"
          />
        </Field>
        <Field label="Where were you born?" helper="City, State, Country." testid="field-s1-birthloc">
          <input className={inputClass} value={form1.birth_location} onChange={update("birth_location")} data-testid="s1-birthloc-input" />
        </Field>
        <Field label="Where are you living now?" helper="City, State, Country." testid="field-s1-living">
          <input className={inputClass} value={form1.living_location} onChange={update("living_location")} data-testid="s1-living-input" />
        </Field>
      </>
    );
  };

  const renderSection2 = () => {
    // Personalised label helper used from screen 3 onwards once the persona
    // has a name (e.g. "How old is Bella?" instead of "How old are they?").
    const theirName = form2.name?.trim();

    if (screen === 1) {
      // Identity fields — asked FIRST so subsequent screen headings/labels
      // can address the persona by name.
      return (
        <>
          <Field label="What is their name or an alias for coaching purposes?" testid="field-s2-name">
            <input className={inputClass} value={form2.name} onChange={update("name")} data-testid="s2-name-input" placeholder="e.g. Bella" />
          </Field>
          <Field label="What is their biological sex?" testid="field-s2-sex">
            <select className={selectClass} value={form2.sex} onChange={update("sex")} data-testid="s2-sex-select">
              <option value="" className={optionClass}>Select...</option>
              {SEX_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} className={optionClass}>{o.label}</option>
              ))}
            </select>
          </Field>
          <Field label="How do they prefer to be addressed?" testid="field-s2-pronouns">
            <select className={selectClass} value={form2.pronouns} onChange={update("pronouns")} data-testid="s2-pronouns-select">
              <option value="" className={optionClass}>Select...</option>
              {PRONOUN_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} className={optionClass}>{o.label}</option>
              ))}
            </select>
            {form2.pronouns === "custom" && (
              <input
                className={`${inputClass} mt-2`}
                value={form2.pronouns_custom}
                onChange={update("pronouns_custom")}
                placeholder="e.g. xe/xir, ze/zir, name only"
                data-testid="s2-pronouns-custom-input"
                autoFocus
              />
            )}
          </Field>
          <Field label="What is their sexual orientation?" testid="field-s2-orientation">
            <select className={selectClass} value={form2.sexual_orientation} onChange={update("sexual_orientation")} data-testid="s2-orientation-select">
              <option value="" className={optionClass}>Select...</option>
              {ORIENTATION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} className={optionClass}>{o.label}</option>
              ))}
            </select>
          </Field>
        </>
      );
    }
    if (screen === 2) {
      // Connection stage — sole focus of this screen. relationship_status was
      // moved to screen 3 so the stage-grid gets full visual real-estate.
      return (
        <Field
          label="How would you rate your connection right now?"
          helper="Pick the stage that best fits today. You can update this any time."
          testid="field-s2-personaconn"
        >
          <StageCardGrid
            value={form2.persona_connection}
            onChange={(v) => updateRaw("persona_connection", v)}
          />
        </Field>
      );
    }
    if (screen === 3) {
      // Relationship label + age. Date-of-birth and time-of-birth were
      // dropped per product simplification.
      return (
        <>
          <Field
            label="What is their relationship with you?"
            helper={relStatusDescription || undefined}
            testid="field-s2-relstatus"
          >
            <select
              className={selectClass}
              value={form2.relationship_status}
              onChange={update("relationship_status")}
              data-testid="s2-relstatus-select"
            >
              <option value="" className={optionClass}>Select...</option>
              {RELATIONSHIP_TYPES.map((o) => (
                <option key={o.value} value={o.value} className={optionClass}>{o.label}</option>
              ))}
            </select>
          </Field>
          <Field label={theirName ? `How old is ${theirName}?` : "How old are they?"} testid="field-s2-age">
            <input
              type="number"
              min="0"
              max="130"
              className={inputClass}
              value={form2.age}
              onChange={update("age")}
              data-testid="s2-age-input"
            />
          </Field>
        </>
      );
    }
    // Screen 4 — locations. Final CTA becomes "CLAIM COINS + START".
    return (
      <>
        <Field label="Where are they living now?" helper="City, State, Country." testid="field-s2-living">
          <input className={inputClass} value={form2.living_location} onChange={update("living_location")} data-testid="s2-living-input" />
        </Field>
        <Field label="Where were they born?" helper="City, State, Country." testid="field-s2-birthloc">
          <input className={inputClass} value={form2.birth_location} onChange={update("birth_location")} data-testid="s2-birthloc-input" />
        </Field>
      </>
    );
  };

  const renderActions = () => {
    const totalScreensThisSection = SECTION_SCREEN_COUNTS[section];
    const isFinalScreen = screen === totalScreensThisSection;
    const finalLabel = section === 1 ? "CREATE PROFILE" : "CLAIM COINS + START";
    const onPrimary = isFinalScreen
      ? section === 1 ? submitSection1 : submitSection2
      : goNext;
    return (
      <div className="flex justify-between items-center mt-6 pt-4 border-t border-divine-gold/15">
        {screen > 1 ? (
          <Button variant="ghost" onClick={goBack} disabled={submitting} data-testid="wizard-back-btn">
            BACK
          </Button>
        ) : (
          <span />
        )}
        <Button
          onClick={onPrimary}
          disabled={submitting}
          className="bg-divine-gold text-divine-black hover:bg-divine-gold/85"
          data-testid={isFinalScreen ? `wizard-submit-section-${section}-btn` : "wizard-next-btn"}
        >
          {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {isFinalScreen ? finalLabel : "NEXT"}
        </Button>
      </div>
    );
  };

  // Suppress Section 1 entirely if user already has a user_persona (e.g. they
  // clicked back into /onboarding after Section 1 succeeded).
  if (skipSection1 && section === 1) return null;

  // Bookend: welcome ceremony (step 0). Only shown to first-time users.
  if (flowStage === "welcome") {
    return (
      <OnboardingWelcome
        userName={userFirstName || "Friend"}
        totalSteps={8}
        onBegin={() => setFlowStage("wizard")}
      />
    );
  }

  // Bookend: closing ceremony (final step). Runs AFTER a successful
  // Section 2 submit; the matchId to navigate to is stashed in state.
  if (flowStage === "closing") {
    return (
      <OnboardingComplete
        userName={userFirstName || "Friend"}
        partnerName={closingPartnerName || "them"}
        onClaim={() => {
          if (closingMatchId) {
            navigate(`/eroscoach/${closingMatchId}`, { replace: true });
          }
        }}
      />
    );
  }

  return (
    <div className="min-h-screen min-h-dvh bg-divine-black text-white flex items-start sm:items-center justify-center px-3 sm:px-6 py-8" data-testid="onboarding-wizard">
      <Card className="bg-divine-charcoal/70 border border-divine-gold/20 w-full max-w-xl">
        <CardContent className="p-5 sm:p-7">
          <ProgressDots section={section} screen={screen} />
          <h1 className="text-2xl sm:text-3xl font-cinzel text-divine-gold mb-5 text-center" data-testid="wizard-heading">
            {section === 1
              ? (screen === 1 ? "About You" : (form1.name?.trim() || "About You"))
              : (form2.name?.trim() || "Your First Relationship")}
          </h1>
          {screen === 1 && intro && (
            <p className="text-white/70 text-sm leading-relaxed mb-6" data-testid="wizard-intro-copy">
              {intro}
            </p>
          )}
          {section === 1 ? renderSection1() : renderSection2()}
          {renderActions()}
        </CardContent>
      </Card>
    </div>
  );
}
